import { useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { ElementRule } from "shared/datamodel/schemas/element-data-layer";
import style from "./element-rules-editor.module.css";
import ElementDynamicRuleCell from "./rule-cell/rule-cell";
import { useDynamicRules } from "../../hooks/use-dynamic-rules";
import { CanvasElement } from "shared/datamodel/schemas";
import classNames from "classnames";
import { useFadeInScrollview } from "frontend/cim-sub-menu/components/scroll-with-fade";

export default function ElementRulesEditor({ elementId, element }: { elementId: string; element: CanvasElement }) {
  const { rules, rulesLoaded, changeRule, deleteRule, duplicateRule } = useDynamicRules(elementId, element, true);
  const rulesContainerRef = useRef<HTMLDivElement>(null);

  const [ruleIds, setRuleIds] = useState<{ id: string; isPlaceholder: boolean }[]>([]);
  const hasInitializedRef = useRef(false);
  const fadeScroll = useFadeInScrollview();
  useEffect(() => {
    if (hasInitializedRef.current || !rulesLoaded) return;

    hasInitializedRef.current = true;
    if (rules.length > 0) {
      setRuleIds(rules.map((rule) => ({ id: rule.id, isPlaceholder: false })));
    } else {
      setRuleIds([{ id: nanoid(10), isPlaceholder: true }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesLoaded]);

  // Ensure we have at least one rule creator when there are no rules
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (rules.length === 0 && !ruleIds.some((id) => id.isPlaceholder)) {
      setRuleIds([{ id: nanoid(10), isPlaceholder: true }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.length, ruleIds.length]);

  function addRuleCreator() {
    setRuleIds((previous) => [...previous, { id: nanoid(10), isPlaceholder: true }]);

    // Scroll to bottom when new rule creator appears
    setTimeout(() => {
      if (rulesContainerRef.current) {
        rulesContainerRef.current.scrollTop = rulesContainerRef.current.scrollHeight;
      }
    }, 10);
  }

  function deleteRuleCreator(id: string) {
    setRuleIds((previous) => previous.filter((ruleId) => ruleId.id !== id));
  }

  function onDeleteRule(rule: ElementRule) {
    deleteRule(rule);
    setRuleIds((previous) => previous.filter((ruleId) => ruleId.id !== rule.id));
  }

  function onChangeRule(rule: ElementRule, isNew: boolean = false) {
    changeRule(rule);
    if (isNew) {
      setRuleIds((previous) =>
        previous.map((ruleId) => (ruleId.id === rule.id ? { ...ruleId, isPlaceholder: false } : ruleId))
      );
    }
  }

  function onDuplicateRule(rule: ElementRule) {
    const newId = nanoid(10);
    duplicateRule(rule, newId);
    setRuleIds((previous) => [...previous, { id: newId, isPlaceholder: false }]);
  }

  return (
    <div className={style.container}>
      <div className={style.padded}>
        <hgroup>
          <h1>Add conditional style</h1>
          <p>Apply rules to control data presentation.</p>
        </hgroup>
      </div>
      <div className={style.listContainer}>
        <ul ref={fadeScroll.onMount} className={classNames(fadeScroll.className)} onScroll={fadeScroll.onScroll}>
          {ruleIds.map((ruleId) => {
            if (ruleId.isPlaceholder) {
              return (
                <li key={ruleId.id}>
                  <ElementDynamicRuleCell
                    elementId={elementId}
                    element={element}
                    newRuleId={ruleId.id}
                    onChange={(rule) => onChangeRule(rule, true)}
                    onDelete={() => deleteRuleCreator(ruleId.id)}
                    isPlaceholder
                  />
                </li>
              );
            }
            const rule = rules.find((rule) => rule.id === ruleId.id);
            if (!rule) return null; // Should never happen
            return (
              <li key={ruleId.id}>
                <ElementDynamicRuleCell
                  elementId={elementId}
                  element={element}
                  rule={rule}
                  onChange={onChangeRule}
                  onDuplicate={() => {
                    onDuplicateRule(rule);
                  }}
                  onDelete={() => {
                    onDeleteRule(rule);
                  }}
                />
              </li>
            );
          })}
        </ul>
        <button className={style.addButton} onClick={addRuleCreator}>
          + Add rule
        </button>
      </div>
    </div>
  );
}
